import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ShoppingCart, ShieldCheck, Truck, Sparkles, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { BrandLogo, BrandTile } from "../components/Brand";

export default function VoucherDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addItem } = useCart();
    const [r, setR] = useState(null);
    const [den, setDen] = useState(null);
    const [qty, setQty] = useState(1);
    const [related, setRelated] = useState([]);
    const [added, setAdded] = useState(false);

    useEffect(() => {
        api.get(`/api/rewards/${id}`).then((res) => {
            setR(res.data);
            setDen(res.data.denominations?.[0] || res.data.min_value);
        });
    }, [id]);

    useEffect(() => {
        if (!r) return;
        api.get(`/api/rewards?category=${encodeURIComponent(r.category)}&limit=8`).then((res) =>
            setRelated((res.data.rewards || []).filter((x) => x.id !== r.id).slice(0, 6))
        );
    }, [r]);

    if (!r) return <div className="p-10 text-center text-[#6b7280]">Loading…</div>;

    const points = Math.round(den * (r.points_per_inr || 4));
    const finalPrice = Math.round(den * (1 - (r.discount_percent || 0) / 100));

    const add = () => {
        addItem(r, den, qty, 0);
        setAdded(true);
        setTimeout(() => setAdded(false), 1500);
    };

    const buyNow = () => {
        addItem(r, den, qty, 0);
        navigate("/cart");
    };

    return (
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
            <div className="flex items-center gap-2 text-xs text-[#6b7280] mb-3">
                <Link to="/" className="hover:text-[#0b1437]">Home</Link>
                <ChevronRight size={12} />
                <Link to={`/category/${encodeURIComponent(r.category)}`} className="hover:text-[#0b1437]">{r.category}</Link>
                <ChevronRight size={12} />
                <Link to={`/brand/${(r.brand_name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="hover:text-[#0b1437]">
                    {r.brand_name}
                </Link>
            </div>

            <div className="grid md:grid-cols-5 gap-6">
                {/* Visual */}
                <div className="md:col-span-2">
                    <div className="aspect-square bg-white rounded-3xl flex items-center justify-center p-10 relative">
                        <BrandLogo url={r.brand_logo_url} name={r.brand_name} size={180} className="!bg-transparent !p-0" />
                        {r.discount_percent > 0 && (
                            <span className="absolute top-4 right-4 px-3 py-1 rounded-full bg-[#ff4d4d] text-white text-xs font-bold">
                                {r.discount_percent}% off
                            </span>
                        )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3">
                        <div className="fx-card p-3 text-center">
                            <Truck size={16} className="mx-auto text-[#c9a959] mb-1" />
                            <p className="text-[10px] text-[#6b7280] uppercase tracking-widest">Instant</p>
                        </div>
                        <div className="fx-card p-3 text-center">
                            <ShieldCheck size={16} className="mx-auto text-[#c9a959] mb-1" />
                            <p className="text-[10px] text-[#6b7280] uppercase tracking-widest">Secure</p>
                        </div>
                        <div className="fx-card p-3 text-center">
                            <Sparkles size={16} className="mx-auto text-[#c9a959] mb-1" />
                            <p className="text-[10px] text-[#6b7280] uppercase tracking-widest">Points back</p>
                        </div>
                    </div>
                </div>

                {/* Buy panel */}
                <div className="md:col-span-3">
                    <span className="fx-chip bg-[#f9fafb] text-[#4b5563] border border-[rgba(11,20,55,0.10)]">{r.category}</span>
                    <h1 className="font-display text-3xl md:text-4xl font-bold mt-2">{r.name}</h1>
                    <p className="text-[#6b7280] text-sm mt-1">By {r.brand_name}</p>

                    <div className="mt-5 flex items-baseline gap-3">
                        <span className="font-display text-4xl font-extrabold text-[#c9a959]">₹{finalPrice}</span>
                        {r.discount_percent > 0 && (
                            <span className="text-[#9ca3af] line-through text-lg">₹{den}</span>
                        )}
                        <span className="text-sm text-[#4b5563]">• Earn {points.toLocaleString("en-IN")} pts back</span>
                    </div>

                    <p className="mt-5 text-xs uppercase tracking-widest text-[#6b7280]">Choose value</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {r.denominations?.map((d) => (
                            <button
                                key={d}
                                data-testid={`vd-den-${d}`}
                                onClick={() => setDen(d)}
                                className={`px-4 py-2 rounded-xl font-semibold transition-all ${
                                    den === d ? "bg-[#c9a959] text-black" : "bg-[#f9fafb] text-[#1f2937] border border-[rgba(11,20,55,0.10)]"
                                }`}
                            >
                                ₹{d}
                            </button>
                        ))}
                    </div>

                    <p className="mt-5 text-xs uppercase tracking-widest text-[#6b7280]">Quantity</p>
                    <div className="flex items-center gap-3 mt-2">
                        <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 rounded-full bg-[#f9fafb] border border-[rgba(11,20,55,0.10)]">−</button>
                        <span className="font-display text-xl font-bold w-10 text-center">{qty}</span>
                        <button onClick={() => setQty(Math.min(10, qty + 1))} className="w-9 h-9 rounded-full bg-[#f9fafb] border border-[rgba(11,20,55,0.10)]">+</button>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-6">
                        <button
                            onClick={add}
                            data-testid="add-to-cart-btn"
                            className="fx-btn-ghost"
                        >
                            <ShoppingCart size={16} /> {added ? "Added!" : "Add to cart"}
                        </button>
                        <button
                            onClick={buyNow}
                            data-testid="buy-now-btn"
                            className="fx-btn-primary"
                        >
                            Buy Now
                        </button>
                    </div>

                    <div className="fx-card p-4 mt-6">
                        <p className="text-xs uppercase tracking-widest text-[#6b7280] mb-2">How redemption works</p>
                        <ul className="text-sm text-[#374151] space-y-1 list-decimal list-inside">
                            <li>Pay with Fundle points + cash via Razorpay.</li>
                            <li>Voucher code & PIN delivered instantly to your account.</li>
                            <li>Redeem on {r.brand_name} {r.redemption_type === "Both" ? "online or in-store" : `(${r.redemption_type})`}.</li>
                            <li>Valid for {r.validity}.</li>
                        </ul>
                    </div>

                    {r.terms && (
                        <details className="fx-card p-4 mt-3">
                            <summary className="cursor-pointer text-sm text-[#374151] font-semibold">Terms & conditions</summary>
                            <p className="text-xs text-[#6b7280] mt-3 leading-relaxed">{r.terms}</p>
                        </details>
                    )}
                </div>
            </div>

            {related.length > 0 && (
                <section className="mt-12">
                    <h2 className="font-display text-xl font-semibold mb-3">You may also like</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {related.map((x) => (
                            <Link
                                to={`/voucher/${x.id}`}
                                key={x.id}
                                className="fx-card hover:border-[#c9a959]/40 overflow-hidden"
                            >
                                <div className="aspect-square">
                                    <BrandTile url={x.brand_logo_url} name={x.brand_name} />
                                </div>
                                <div className="p-2">
                                    <p className="text-xs font-semibold truncate">{x.brand_name}</p>
                                    <p className="text-[10px] text-[#6b7280]">From ₹{x.min_value}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
